/** MoneyMax Returning Visitor Tray V1.1 — site-wide inactivity signal, homepage-only tray. */
(() => {
  'use strict';

  const STORAGE_KEY = 'mmx_recently_viewed_products_v1';
  const SESSION_KEY = 'mmx_returning_visitor_shown_v1';
  const ACTIVITY_KEY = 'mmx_returning_visitor_last_active_v1';
  const ACTIVITY_THROTTLE_MS = 15000;
  let lastActivityWriteAt = 0;
  let onVisibleReturn = null;

  function readLastActivity() {
    try {
      const value = Number(window.localStorage.getItem(ACTIVITY_KEY));
      const now = Date.now();
      return Number.isFinite(value) && value > 0 && value <= now ? value : 0;
    } catch (_error) {
      return 0;
    }
  }

  // Capture inactivity BEFORE recording the page visit. This snapshot is used
  // only by the homepage tray. Every page records activity for future visits.
  const previousActivity = readLastActivity();
  let observedAwayMs = previousActivity ? Date.now() - previousActivity : -1;

  function recordActivity(force = false) {
    if (!force && document.visibilityState === 'hidden') return;
    const now = Date.now();
    if (!force && now - lastActivityWriteAt < ACTIVITY_THROTTLE_MS) return;
    try {
      window.localStorage.setItem(ACTIVITY_KEY, String(now));
      lastActivityWriteAt = now;
    } catch (_error) {
      // Storage is optional; inaccessible storage must never block shopping.
    }
  }

  function resumeVisiblePage() {
    // The last activity could be from another open tab: check it before stamping.
    const previous = readLastActivity();
    const gap = previous ? Date.now() - previous : -1;
    recordActivity(true);
    if (gap >= 0 && typeof onVisibleReturn === 'function') onVisibleReturn(gap);
  }

  recordActivity(true);
  for (const eventName of ['pointerdown', 'keydown', 'touchstart', 'scroll']) {
    document.addEventListener(eventName, () => recordActivity(), { passive: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') recordActivity(true);
    else resumeVisiblePage();
  });
  window.addEventListener('pagehide', () => recordActivity(true));
  window.addEventListener('pageshow', (event) => {
    // A back/forward cache restore does not execute the script again.
    if (event.persisted && document.visibilityState !== 'hidden') resumeVisiblePage();
  });

  const root = document.querySelector('[data-mmx-returning-visitor]');
  if (!root) return; // Non-homepage pages only run the tiny activity tracker.

  const viewport = root.querySelector('[data-mmx-rv-viewport]');
  const track = root.querySelector('[data-mmx-rv-track]');
  const close = root.querySelector('[data-mmx-rv-close]');
  const previous = root.querySelector('[data-mmx-rv-prev]');
  const next = root.querySelector('[data-mmx-rv-next]');
  if (!viewport || !track || !close || !previous || !next) return;

  const clamp = (number, min, max) => Math.max(min, Math.min(max, number));
  const numberSetting = (name, fallback, min, max) => {
    const value = Number(root.dataset[name]);
    return Number.isFinite(value) ? clamp(value, min, max) : fallback;
  };
  const settings = {
    maxProducts: numberSetting('maxProducts', 5, 2, 8),
    minProducts: numberSetting('minProducts', 2, 1, 8),
    retentionDays: numberSetting('retentionDays', 30, 1, 60),
    minMinutesAway: numberSetting('minMinutesAway', 30, 0, 180),
    delayMs: numberSetting('delaySeconds', 3, 0, 15) * 1000,
    testMode: root.dataset.testMode === 'true',
    showPrices: root.dataset.showPrices !== 'false',
    oncePerSession: root.dataset.oncePerSession !== 'false'
  };

  let open = false;
  let resizeTimer = 0;

  const themeRoot = () => (
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
  );

  function readHistory() {
    let raw;
    try {
      raw = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (_error) {
      return [];
    }
    if (!Array.isArray(raw)) return [];

    const now = Date.now();
    const threshold = now - settings.retentionDays * 86400000;
    const seen = new Set();
    return raw.filter((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const id = Number(entry.id || 0);
      const handle = String(entry.handle || '').trim().toLowerCase();
      const at = Number(entry.viewedAt || 0);
      if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return false;
      if (!Number.isFinite(at) || at < threshold || at > now + 300000) return false;
      const key = id > 0 ? `id:${id}` : `handle:${handle}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).sort((a, b) => Number(b.viewedAt) - Number(a.viewedAt)).slice(0, 24);
  }

  function alreadyShown() {
    if (!settings.oncePerSession || settings.testMode) return false;
    try { return window.sessionStorage.getItem(SESSION_KEY) === '1'; }
    catch (_error) { return false; }
  }

  function markShown() {
    if (!settings.oncePerSession || settings.testMode) return;
    try { window.sessionStorage.setItem(SESSION_KEY, '1'); }
    catch (_error) { /* Storage is optional. */ }
  }

  function money(amountCents) {
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    const currency = String(active || root.dataset.currency || 'SGD');
    const locale = document.documentElement.lang || 'en-SG';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(amountCents / 100);
    } catch (_error) {
      return `$${(amountCents / 100).toFixed(2)}`;
    }
  }

  function safeImageUrl(source) {
    const candidate = typeof source === 'string' ? source : source && source.src;
    if (!candidate) return '';
    try {
      const url = new URL(candidate, window.location.href);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      // Shopify CDN image resizing, no forced network work for unavailable images.
      if (/(^|\.)cdn\.shopify\.com$/i.test(url.hostname)) url.searchParams.set('width', '320');
      return url.href;
    } catch (_error) {
      return '';
    }
  }

  function productSummary(product, originalHandle) {
    if (!product || String(product.handle || '').toLowerCase() !== originalHandle) return null;
    const handle = String(product.handle || '').toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    if (handle.startsWith('docapp-free-gift-')) return null;
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
    if (tags.some((tag) => String(tag).trim().toLowerCase() === '__hidden')) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const available = variants.filter((variant) => variant && variant.available && Number(variant.price) > 0);
    if (!available.length) return null;
    const prices = available.map((variant) => Number(variant.price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const image = safeImageUrl(product.featured_image || product.images && product.images[0]);
    if (!image || !String(product.title || '').trim()) return null;
    const prefix = minPrice < maxPrice ? 'From ' : '';
    return { handle, title: String(product.title), image, price: prefix + money(minPrice) };
  }

  async function fetchProduct(entry) {
    const handle = String(entry.handle).toLowerCase();
    const url = `${themeRoot()}products/${encodeURIComponent(handle)}.js`;
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;
      return productSummary(await response.json(), handle);
    } catch (_error) {
      return null;
    }
  }

  async function loadProducts(entries) {
    const result = [];
    const candidates = entries.slice(0, Math.max(8, settings.maxProducts * 2));
    // Limit network pressure: at most three product requests concurrently.
    for (let i = 0; i < candidates.length && result.length < settings.maxProducts; i += 3) {
      const batch = await Promise.all(candidates.slice(i, i + 3).map(fetchProduct));
      for (const product of batch) {
        if (product && result.length < settings.maxProducts) result.push(product);
      }
    }
    return result;
  }

  function makeCard(product) {
    const item = document.createElement('a');
    item.className = 'mmx-rv__card';
    item.href = `${themeRoot()}products/${encodeURIComponent(product.handle)}`;
    item.setAttribute('aria-label', `View ${product.title}`);

    const media = document.createElement('span');
    media.className = 'mmx-rv__media';
    const image = document.createElement('img');
    image.src = product.image;
    image.alt = product.title;
    image.width = 160;
    image.height = 160;
    image.loading = 'lazy';
    image.decoding = 'async';
    media.appendChild(image);

    const info = document.createElement('span');
    info.className = 'mmx-rv__info';
    const title = document.createElement('span');
    title.className = 'mmx-rv__product-title';
    title.textContent = product.title;
    info.appendChild(title);
    if (settings.showPrices) {
      const price = document.createElement('span');
      price.className = 'mmx-rv__price';
      price.textContent = product.price;
      info.appendChild(price);
    }
    item.append(media, info);
    return item;
  }

  function updateArrows() {
    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const overflow = max > 3;
    previous.hidden = !overflow;
    next.hidden = !overflow;
    previous.disabled = !overflow || viewport.scrollLeft <= 3;
    next.disabled = !overflow || viewport.scrollLeft >= max - 3;
  }

  function shift(direction) {
    const card = track.querySelector('.mmx-rv__card');
    const distance = card ? card.getBoundingClientRect().width + 12 : 220;
    viewport.scrollBy({ left: direction * distance * 2, behavior: 'smooth' });
  }

  function positionBelowHeader() {
    const header = document.getElementById('shopify-section-header');
    let offset = 0;
    if (header) {
      const rect = header.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) offset = clamp(rect.bottom, 0, 250);
    }
    root.style.setProperty('--mmx-rv-header-bottom', `${Math.round(offset)}px`);
  }

  function dismiss() {
    if (!open) return;
    open = false;
    markShown();
    root.classList.remove('mmx-rv--open');
    root.setAttribute('aria-hidden', 'true');
    const hide = () => { if (!open) root.hidden = true; };
    root.addEventListener('transitionend', hide, { once: true });
    window.setTimeout(hide, 600);
  }

  function show(products) {
    if (alreadyShown() || !products.length) return;
    track.replaceChildren(...products.map(makeCard));
    positionBelowHeader();
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    updateArrows();
    // Keep focus in the existing page; the tray is a non-modal region.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => root.classList.add('mmx-rv--open'));
    });
    open = true;
    markShown();
  }

  close.addEventListener('click', dismiss);
  previous.addEventListener('click', () => shift(-1));
  next.addEventListener('click', () => shift(1));
  viewport.addEventListener('scroll', updateArrows, { passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) dismiss();
  });
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => { positionBelowHeader(); updateArrows(); }, 100);
  }, { passive: true });

  let startPending = false;
  let loadingProducts = false;
  async function start() {
    if (loadingProducts || alreadyShown() || open || document.visibilityState === 'hidden') return;
    if (!settings.testMode && (observedAwayMs < 0 || observedAwayMs < settings.minMinutesAway * 60000)) return;
    const entries = readHistory();
    if (entries.length < settings.minProducts) return;
    loadingProducts = true;
    try {
      const products = await loadProducts(entries);
      if (document.visibilityState !== 'hidden' && products.length >= settings.minProducts) show(products);
    } finally {
      loadingProducts = false;
    }
  }

  function begin() {
    if (startPending || alreadyShown() || open) return;
    startPending = true;
    window.setTimeout(() => {
      startPending = false;
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => { start().catch(() => {}); }, { timeout: 1500 });
      } else {
        start().catch(() => {});
      }
    }, settings.delayMs);
  }

  onVisibleReturn = (gapMs) => {
    if (gapMs >= settings.minMinutesAway * 60000) {
      observedAwayMs = gapMs;
      begin();
    }
  };

  if (document.readyState === 'complete') begin();
  else window.addEventListener('load', begin, { once: true });
})();
